import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Button, Input, Spinner } from "../components/ui";
import api from "../lib/api";
import { Globe, Sun, Moon, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { t, toggleLang, lang, toggleTheme, theme } = useApp();
  const navigate = useNavigate();

  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const tok = new URLSearchParams(window.location.search).get("token") || "";
    setToken(tok);
    if (!tok) setError(t("auth_reset_invalid"));
  }, [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError(t("auth_password_placeholder") + " — min 8");
      return;
    }
    if (password !== confirm) {
      setError(t("auth_passwords_no_match"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || t("auth_error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-background p-8 relative">
      <div className="absolute top-6 right-6 flex items-center gap-2">
        <button onClick={toggleLang} data-testid="reset-lang-toggle"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium text-text-secondary hover:text-text-primary transition-colors">
          <Globe className="w-3.5 h-3.5" /> {lang.toUpperCase()}
        </button>
        <button onClick={toggleTheme} data-testid="reset-theme-toggle"
          className="inline-flex items-center justify-center w-8 h-8 rounded-full border text-text-secondary hover:text-text-primary transition-colors">
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
      </div>

      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2.5 mb-10 group w-fit" data-testid="reset-logo-home">
          <div className="w-9 h-9 rounded-xl bg-text-primary text-background flex items-center justify-center font-heading font-black transition-transform group-hover:scale-105">
            CO
          </div>
          <span className="font-heading font-bold">{t("app_name")}</span>
        </Link>

        {done ? (
          <div className="space-y-5" data-testid="reset-success">
            <div className="flex items-center gap-2 text-text-primary">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <p className="text-sm">{t("auth_reset_success")}</p>
            </div>
            <Button size="lg" className="w-full" onClick={() => navigate("/login", { replace: true })}
              data-testid="reset-go-signin">
              {t("auth_go_to_signin")}
            </Button>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-heading font-medium mb-1">{t("auth_reset_title")}</h2>
            <p className="text-text-secondary text-sm mb-8">{t("auth_reset_subtitle")}</p>

            <form onSubmit={handleSubmit} className="space-y-3" data-testid="reset-form">
              <Input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("auth_new_password")}
                autoComplete="new-password"
                disabled={!token}
                data-testid="reset-password-input"
              />
              <Input
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={t("auth_confirm_password")}
                autoComplete="new-password"
                disabled={!token}
                data-testid="reset-confirm-input"
              />

              {error && (
                <p className="text-xs text-red-400" data-testid="reset-error">{error}</p>
              )}

              <Button type="submit" size="lg" className="w-full"
                disabled={submitting || !token} data-testid="reset-submit-button">
                {submitting && <Spinner className="w-4 h-4" />}
                {t("auth_reset_cta")}
              </Button>
            </form>

            <p className="text-sm text-text-secondary text-center mt-6">
              <button type="button" onClick={() => navigate("/login")}
                className="text-text-primary font-medium hover:underline" data-testid="reset-back-signin">
                {t("auth_back_to_signin")}
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
